import { PageController } from '@client/components/_core/layout/PageController';
import { Emails } from '@client/components/_shared/email/Emails';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { useTrackEngagementView } from '@client/hooks/business/engagement/useTrackEngagementView';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useAreFeatureFlagsSettled, useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG } from '@shared/constants/business/communication';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
    selectedId: z.coerce.bigint().optional(),
});

export const Route = createFileRoute('/console/$orgId/workload-manager/emails')({
    staticData: {
        metadata: {
            title: 'Emails',
        },
        ui: {
            heightVariant: 'fit',
        },
    },
    validateSearch: zodValidator(searchSchema),
    component: EmailsRouteGuard,
});

/**
 * The flag is read in render rather than in `beforeLoad`: `getFlag` answers with the default
 * before PostHog has evaluated flags for the signed-in user and organisation, so a cold deep link
 * would be bounced to Tasks (losing `selectedId`) in an organisation that has Emails enabled.
 * Wait until evaluation settles for this identity, then either mount Emails or redirect.
 */
function EmailsRouteGuard() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const user = useUser({ throwOnUndefined: false });
    const areFlagsSettled = useAreFeatureFlagsSettled({ userId: user?.id.toString(), orgId: orgId.toString() });
    const emailsEnabled = useFeatureFlag(ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG, false);
    const shouldRedirect = areFlagsSettled && !emailsEnabled;

    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    useEffect(() => {
        if (shouldRedirect) {
            navigateRef.current({ to: '/console/$orgId/workload-manager/tasks', params: { orgId }, replace: true });
        }
    }, [shouldRedirect, orgId]);

    if (!emailsEnabled) {
        return areFlagsSettled ? null : <Loading />;
    }
    return <EmailsRoute />;
}

function EmailsRoute() {
    useTrackEngagementView('workload_tab_viewed', { tab: 'emails' });
    const navigate = useNavigate();
    const orgId = useOrgId();

    const { selectedId: urlSelectedId } = Route.useSearch();
    const [localSelectedId, setLocalSelectedId] = useState<bigint | undefined>();

    useEffect(() => {
        if (urlSelectedId !== localSelectedId) {
            setLocalSelectedId(urlSelectedId);
        }
    }, [urlSelectedId]);

    const handleSelectedIdChange = useCallback(
        (id: bigint | undefined) => {
            setLocalSelectedId(id);
            navigate({
                to: '/console/$orgId/workload-manager/emails',
                params: { orgId },
                search: { selectedId: id },
            });
        },
        [navigate, orgId],
    );

    return (
        <PageController route={Route}>
            <Emails selectedId={localSelectedId} onSelectedIdChange={handleSelectedIdChange} />
        </PageController>
    );
}
